import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, CheckCircle2, Clock3, MapPin, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

const SERVICES = [
  "Event Photography",
  "Foto Booth Experience",
  "Real Estate Media",
  "Restaurant / Food Photography",
  "Airbnb / Hospitality Media",
  "Business Content Package",
  "Monthly Content Retainer",
  "Event Recap Video",
  "Instant Keepsakes",
  "Open Bar for Photos",
  "Brand / Event Activation",
  "Portrait Session",
  "Other / Custom",
];

const DURATIONS = ["1 hour", "2 hours", "3 hours", "4 hours", "5+ hours / custom"];

function encode(data) {
  return new URLSearchParams(data).toString();
}

export default function Book() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("sending");
    setError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "booking-request", ...payload }),
      });
      if (!response.ok) throw new Error("Could not submit booking request.");
      form.reset();
      navigate("/book/success", { replace: true });
    } catch (err) {
      setStatus("error");
      setError(err?.message || "Something went wrong. Please try again.");
    }
  }

  return (
    <div className="booking-page min-h-screen bg-[#070707] text-[#f5f0e7]">
      <Seo
        title="Book Fotos on the Geaux | New Orleans Photography & Video"
        description="Book Fotos on the Geaux for event photography, Foto Booth, real estate media, food photography, hospitality content, business content, video and instant prints."
        path="/book"
      />

      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[.2em] text-white/65 hover:text-white">
            <ArrowLeft size={14} /> FOTOS HOME
          </Link>
          <span className="font-accent text-[10px] tracking-[.24em] text-[#c8aa82]">BOOK FOTOS</span>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl min-w-0 gap-10 px-4 py-10 sm:px-5 sm:py-12 md:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)] md:gap-12 md:py-20">
        <section>
          <p className="max-w-full font-accent text-[10px] tracking-[.22em] text-[#c8aa82] sm:tracking-[.34em]">NEW ORLEANS · METAIRIE · GULF SOUTH</p>
          <h1 className="mt-5 font-display text-6xl font-black leading-[.86] tracking-[-.04em] sm:text-7xl">Put your project on the books.</h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-white/60">Choose the service, date and preferred start time. I’ll review the details and follow up to confirm availability, final scope and payment.</p>

          <div className="mt-10 grid gap-3">
            <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[.03] p-4"><CalendarDays size={18} className="mt-0.5 text-[#c8aa82]"/><div><p className="font-medium">Pick your preferred date</p><p className="mt-1 text-sm text-white/45">Submit the project or event date you want.</p></div></div>
            <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[.03] p-4"><Clock3 size={18} className="mt-0.5 text-[#c8aa82]"/><div><p className="font-medium">Choose your coverage</p><p className="mt-1 text-sm text-white/45">1–4 hours or a custom production window.</p></div></div>
            <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[.03] p-4"><MapPin size={18} className="mt-0.5 text-[#c8aa82]"/><div><p className="font-medium">Tell me where</p><p className="mt-1 text-sm text-white/45">New Orleans, Metairie and travel-ready bookings.</p></div></div>
            <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[.03] p-4"><Sparkles size={18} className="mt-0.5 text-[#c8aa82]"/><div><p className="font-medium">Build the package</p><p className="mt-1 text-sm text-white/45">Combine photo, video, Foto Booth, instant prints or recurring content.</p></div></div>
          </div>

          <div className="mt-6 rounded-2xl border border-[#c8aa82]/20 bg-[#c8aa82]/[.06] p-4 text-sm leading-6 text-white/55">
            Drone and aerial media is planned for 2027 and is not currently available for booking.
          </div>
        </section>

        <section className="rounded-[28px] border border-white/10 bg-[#0d0d0d] p-5 shadow-[0_30px_100px_rgba(0,0,0,.5)] sm:p-8">
          {status === "success" ? (
            <div className="flex min-h-[520px] flex-col items-center justify-center text-center">
              <CheckCircle2 size={42} className="text-[#c8aa82]" />
              <h2 className="mt-5 font-display text-4xl font-black">Booking request received.</h2>
              <p className="mt-4 max-w-md text-white/55">Your request has been submitted. I’ll confirm availability and next steps using the contact information you provided.</p>
              <button onClick={() => setStatus("idle")} className="mt-7 rounded-full border border-white/20 px-6 py-3 font-accent text-[10px] tracking-[.2em]">SUBMIT ANOTHER REQUEST</button>
            </div>
          ) : (
            <form name="booking-request" method="POST" data-netlify="true" netlify-honeypot="company-fax" onSubmit={handleSubmit} className="space-y-5">
              <input type="hidden" name="form-name" value="booking-request" />
              <p className="hidden"><label>Do not fill this out: <input name="company-fax" /></label></p>

              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Name *</span><input required name="name" autoComplete="name" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Phone *</span><input required name="phone" type="tel" autoComplete="tel" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
              </div>

              <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Email *</span><input required name="email" type="email" autoComplete="email" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>

              <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Service *</span><select required name="service" defaultValue="" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]"><option value="" disabled>Select a service</option>{SERVICES.map((item) => <option key={item}>{item}</option>)}</select></label>

              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Project / event date *</span><input required name="event-date" type="date" min={minDate} className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Preferred start time *</span><input required name="start-time" type="time" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
              </div>

              <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Coverage length *</span><select required name="duration" defaultValue="" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]"><option value="" disabled>Select duration</option>{DURATIONS.map((item) => <option key={item}>{item}</option>)}</select></label>

              <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Venue / location *</span><input required name="location" placeholder="Venue, business, property or address" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>

              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Estimated guests</span><input name="guest-count" type="number" min="1" placeholder="If applicable" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
                <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Budget range</span><input name="budget" placeholder="Optional" className="w-full rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>
              </div>

              <label className="block"><span className="mb-2 block text-xs uppercase tracking-[.16em] text-white/45">Tell me about the project</span><textarea name="details" rows="5" placeholder="What are we creating? Where will the content be used? What does a successful shoot look like?" className="w-full resize-y rounded-xl border border-white/10 bg-black px-4 py-3 outline-none focus:border-[#c8aa82]" /></label>

              {error && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</p>}

              <button disabled={status === "sending"} type="submit" className="w-full rounded-full bg-[#f2eee4] px-6 py-4 font-accent text-[11px] tracking-[.22em] text-black transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60">{status === "sending" ? "SENDING..." : "REQUEST THIS DATE"}</button>
              <p className="text-center text-xs leading-5 text-white/35">Submitting a request does not hold the date until availability and payment are confirmed.</p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
